import { Button } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { useMemo } from "react";
import routeService from "services/RouteService";
import TournamentStandingsController from "./TournamentStandingsController";

const SwissStandings = () => {
  const controller = TournamentStandingsController.use();
  const { competition, entries, standingsData } = controller.state;
  if (!standingsData) return null;

  // Combine standings data with entry information to get player names
  const displayStandings = useMemo(() => {
    // Swiss format has a single "All" group
    const allGroup = standingsData.groups.All || [];
    return allGroup.map((entry) => {
      // Find the tournament entry by ID
      const tournamentEntry = entries.findById(entry.entryId);

      // Get the name from the entry or use a fallback
      let name = "Unknown Player";
      let seed: number | null = null;
      if (tournamentEntry) {
        name = tournamentEntry.shortNames.join(", ") || `Entry #${tournamentEntry.seed || "?"}`;
        seed = tournamentEntry.seed ?? null;
      }

      // Return the display entry with name and seed included
      return {
        ...entry,
        name,
        seed,
      };
    });
  }, [standingsData, controller.state.entries]);

  if (displayStandings.length === 0) {
    return <div className="py-4 text-center text-gray-500">No standings data available</div>;
  }

  return (
    <div className="overflow-hidden border border-gray-200 rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" data-testid="standings-table-all">
          <thead className="bg-gray-50">
            <tr className="*:px-4 *:py-2">
              <th
                scope="col"
                className="px-2! text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Pos
              </th>
              <th
                scope="col"
                className="px-2! text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Seed
              </th>
              <th
                scope="col"
                className="text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Name
              </th>
              <th
                scope="col"
                className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Played
              </th>
              <th
                scope="col"
                className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Won
              </th>
              <th
                scope="col"
                className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Lost
              </th>
              <th
                scope="col"
                className="text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Points
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayStandings.map((entry, index) => (
              <tr key={entry.entryId} className="*:px-4 *:py-2 even:bg-white odd:bg-gray-50">
                <td className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  {entry.position ?? index + 1}
                </td>
                <td className="px-4 py-2 text-sm text-center text-gray-500 whitespace-nowrap">
                  {entry.seed ?? "—"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  <Button
                    kind="link"
                    onClick={() =>
                      routeService.openModal("/tournament/:id/entries/:entryId/edit", {
                        params: { id: competition?.tournament_session_id ?? "", entryId: entry.entryId },
                      })
                    }
                  >
                    {entry.name}
                  </Button>
                </td>
                <td className="px-4 py-2 text-sm text-center text-gray-500 whitespace-nowrap">
                  {entry.played}
                </td>
                <td className="px-4 py-2 text-sm text-center text-gray-500 whitespace-nowrap">
                  {entry.wins}
                </td>
                <td className="px-4 py-2 text-sm text-center text-gray-500 whitespace-nowrap">
                  {entry.losses}
                </td>
                <td
                  className={clsx(
                    "px-4 py-2 whitespace-nowrap text-sm font-medium text-center",
                    index === 0 ? "text-green-600" : "text-gray-900",
                  )}
                >
                  {entry.points}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApplicationView(SwissStandings);
